import { useState, useEffect, useRef, useCallback } from 'react';
import { ElementPickerOverlay } from './ElementPickerOverlay';
import { useAnimationStore } from '../stores/animationStore';
import { createElementAnimation, generateCategoryCSS, injectAnimationKeyframes, applyFireflyAnimation, generateFireflyCSS } from '../utils/elementAnimation';

export function ElementPicker() {
  const { currentPreset } = useAnimationStore();
  const [isPicking, setIsPicking] = useState(false);
  const selectedRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const originalStylesRef = useRef<string>('');

  const applyAnimation = useCallback((element: HTMLElement) => {
    if (!currentPreset) return;

    cleanupRef.current?.();

    const category = currentPreset.category;
    const config = currentPreset.config;
    const subType = currentPreset.subType;

    originalStylesRef.current = element.getAttribute('style') || '';

    if (category === 'backgrounds') {
      cleanupRef.current = createElementAnimation(element, subType, config);
    } else if (category === 'buttons' && subType === 'firefly') {
      // Firefly uses canvas overlay for precise positioning
      element.style.cssText = originalStylesRef.current + generateFireflyCSS(config);
      cleanupRef.current = applyFireflyAnimation(element, config);
    } else {
      injectAnimationKeyframes();
      const css = generateCategoryCSS(category, config);
      element.style.cssText = originalStylesRef.current + css;
    }

    console.log('Applied:', category, subType, config);
  }, [currentPreset]);

  const clearAnimation = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (selectedRef.current) {
      selectedRef.current.style.cssText = originalStylesRef.current;
    }
    selectedRef.current = null;
    originalStylesRef.current = '';
  }, []);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (selectedRef.current && currentPreset) {
      cleanupRef.current?.();
      const category = currentPreset.category;
      const config = currentPreset.config;
      const subType = currentPreset.subType;

      if (category === 'backgrounds') {
        cleanupRef.current = createElementAnimation(selectedRef.current, subType, config);
      }

      console.log('Updated:', category, subType, config);
    }
  }, [currentPreset]);

  useEffect(() => {
    if (!isPicking) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target !== document.body && target !== document.documentElement && target.tagName !== 'STYLE') {
        e.preventDefault();
        e.stopPropagation();

        clearAnimation();
        selectedRef.current = target;
        applyAnimation(target);
        setIsPicking(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPicking(false);
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.cursor = 'crosshair';

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.cursor = '';
    };
  }, [isPicking, applyAnimation, clearAnimation]);

  return (
    <>
      <button
        onClick={() => setIsPicking(!isPicking)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: isPicking ? '#e74c3c' : '#3498db',
          border: 'none',
          borderRadius: '6px',
          color: '#fff',
          fontSize: '13px',
          cursor: 'pointer'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isPicking ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          )}
        </svg>
        {isPicking ? 'Отмена (ESC)' : 'Выбрать элемент'}
      </button>

      {selectedRef.current && !isPicking && (
        <button
          onClick={clearAnimation}
          style={{
            padding: '8px 12px',
            background: '#95a5a6',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '13px',
            cursor: 'pointer'
          }}
        >
          Очистить
        </button>
      )}

      <ElementPickerOverlay isActive={isPicking} />
    </>
  );
}